import React, { useState } from 'react';
import { toast } from 'sonner';
import { MessageSquarePlus, Loader2, CheckCircle2, Bug, Lightbulb, HelpCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { videoApiClient } from '@/services/videoApi';
import { trackEvent } from '@/lib/analytics';
import { cn } from '@/lib/utils';

const CATEGORIES = [
  { value: 'Bug', label: 'Bug', icon: Bug },
  { value: 'Idea', label: 'Idea', icon: Lightbulb },
  { value: 'Question', label: 'Question', icon: HelpCircle },
];

/**
 * Floating "Feedback" button → modal → posts to the back-end, which pings the
 * team's Google Chat (same mechanism as the CTV leads). Self-contained; mount
 * once in the authenticated shell.
 */
export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('Idea');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) {
      // Reset after the close animation.
      setTimeout(() => {
        setCategory('Idea');
        setMessage('');
        setEmail('');
        setSubmitted(false);
      }, 200);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!message.trim()) {
      toast.error('Please add a short message.');
      return;
    }
    setSubmitting(true);
    try {
      await videoApiClient.submitFeedback({
        message: message.trim(),
        category,
        email: email.trim() || null,
        page_url: window.location.pathname,
      });
      trackEvent('feedback_submitted', { category });
      setSubmitted(true);
    } catch (err) {
      toast.error('Could not send your feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Send feedback"
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-foreground px-4 py-3 text-sm font-medium text-background shadow-lg transition hover:opacity-90"
      >
        <MessageSquarePlus className="h-4 w-4" />
        <span className="hidden sm:inline">Feedback</span>
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          {submitted ? (
            <div className="flex flex-col items-center text-center py-8 space-y-3">
              <CheckCircle2 className="h-12 w-12 text-teal" />
              <DialogTitle>Thanks for the feedback!</DialogTitle>
              <DialogDescription>
                We read every message and will follow up if you left an email.
              </DialogDescription>
              <Button className="mt-2" onClick={() => handleOpenChange(false)}>Done</Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Send feedback</DialogTitle>
                <DialogDescription>
                  Found a bug, have an idea, or need help? It goes straight to the team.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4 pt-2">
                <div className="flex gap-2">
                  {CATEGORIES.map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setCategory(value)}
                      className={cn(
                        'flex flex-1 items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm transition',
                        category === value
                          ? 'border-coral bg-coral/10 text-coral'
                          : 'border-border text-muted-foreground hover:bg-muted',
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {label}
                    </button>
                  ))}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="feedback-message">Message</Label>
                  <Textarea
                    id="feedback-message"
                    rows={4}
                    required
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="What happened, or what would make this better?"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="feedback-email">Email (optional)</Label>
                  <Input
                    id="feedback-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="So we can follow up"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-1">
                  <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      'Send'
                    )}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
